import { useState } from 'react';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { Dropdown, Menu, Input, type DropDownProps, type MenuProps } from 'antd';
import { StyledButton, type StyledButtonProps } from '@app/components/button/Button';
import type { ReactNode } from 'react';
import type { ItemType } from 'antd/es/menu/interface';
import type { AnyObject } from 'antd/es/_util/type';
import { MdSearch } from 'react-icons/md';

export type ActionsDropdownProps = DropDownProps & {
    title: ReactNode;
    items: ItemType[];
    data?: AnyObject;
    disabled?: boolean;
    buttonProps?: StyledButtonProps;
    searchable?: boolean;
};

type SearchableMenuPanelProps = {
    items: ItemType[];
    initialKeys: string[];
    selectable?: boolean;
    multiple?: boolean;
    onApply: (keys: string[]) => void;
    onClear: () => void;
};

const SearchableMenuPanel = ({
    items,
    initialKeys,
    selectable,
    multiple,
    onApply,
    onClear,
}: SearchableMenuPanelProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [stagedKeys, setStagedKeys] = useState<string[]>(initialKeys);

    const selectableItems = items.filter((item) => {
        if (!item) return false;
        if ('type' in item && item.type === 'divider') return false;
        if ('key' in item && item.key === 'reset') return false;
        return true;
    });

    const filteredItems = searchQuery
        ? selectableItems.filter((item) => {
              if (item && 'label' in item && typeof item.label === 'string') {
                  return item.label.toLowerCase().includes(searchQuery.toLowerCase());
              }
              return true;
          })
        : selectableItems;

    return (
        <div className="styled-dropdown-searchable">
            <div className="styled-dropdown-searchable__input">
                <Input
                    prefix={<MdSearch size={14} />}
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    allowClear
                />
            </div>
            <div className="styled-dropdown-searchable__list">
                <Menu
                    selectable={selectable}
                    selectedKeys={stagedKeys}
                    multiple={multiple}
                    onSelect={({ selectedKeys }) => setStagedKeys(selectedKeys as string[])}
                    onDeselect={({ selectedKeys }) => setStagedKeys(selectedKeys as string[])}
                    items={filteredItems}
                />
            </div>
            <div className="styled-dropdown-searchable__footer">
                <StyledButton type="text" color="red" size="small" onClick={onClear}>
                    Clear
                </StyledButton>
                <StyledButton
                    type="primary"
                    color="brand"
                    size="small"
                    shape="round"
                    onClick={() => onApply(stagedKeys)}>
                    Apply
                </StyledButton>
            </div>
        </div>
    );
};

export const ActionsDropdown = ({
    items = [],
    title = '',
    data = {},
    disabled = false,
    buttonProps,
    menu,
    searchable = false,
    ...props
}: ActionsDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openCount, setOpenCount] = useState(0);

    const DropdownIcon = isOpen ? IoChevronUp : IoChevronDown;

    if (searchable) {
        const commitSelection = (keys: string[]) => {
            menu?.onSelect?.({ selectedKeys: keys } as Parameters<
                NonNullable<MenuProps['onSelect']>
            >[0]);
            setIsOpen(false);
        };

        return (
            <Dropdown
                {...props}
                open={isOpen}
                classNames={{ root: 'styled-dropdown' }}
                trigger={['click']}
                placement="bottomLeft"
                onOpenChange={(open, info) => {
                    if (info.source === 'menu') return;
                    setIsOpen(open);
                    if (open) setOpenCount((count) => count + 1);
                }}
                disabled={disabled}
                popupRender={() => (
                    <SearchableMenuPanel
                        key={openCount}
                        items={items}
                        initialKeys={(menu?.selectedKeys as string[]) ?? []}
                        selectable={menu?.selectable}
                        multiple={menu?.multiple}
                        onApply={commitSelection}
                        onClear={() => commitSelection([])}
                    />
                )}>
                <StyledButton
                    type="primary"
                    data-testid={`actions-dropdown-button-${data?.id}`}
                    icon={<DropdownIcon size="14" style={{ display: 'block' }} />}
                    iconPlacement="end"
                    {...buttonProps}>
                    {title}
                </StyledButton>
            </Dropdown>
        );
    }

    return (
        <Dropdown
            {...props}
            classNames={{ root: 'styled-dropdown' }}
            menu={{
                ...menu,
                items,
                onClick: () => setIsOpen(false), // close menu on click
                style: {
                    minWidth: 100,
                },
            }}
            trigger={['click']}
            onOpenChange={(open) => setIsOpen(open)}
            disabled={disabled}>
            <StyledButton
                type="primary"
                data-testid={`actions-dropdown-button-${data?.id}`}
                icon={<DropdownIcon size="14" style={{ display: 'block' }} />}
                iconPlacement="end"
                {...buttonProps}>
                {title}
            </StyledButton>
        </Dropdown>
    );
};
